import {
  CREATE_SEARCH,
  CREATE_SEARCH_SESSION,
  FETCH_RECENT_SEARCHES,
  SET_RECENT_SEARCHES,
  TYPEAHEAD_STREAMING_START,
  TYPEAHEAD_STREAMING_UPDATE,
  TYPEAHEAD_STREAMING_COMPLETE,
  TYPEAHEAD_STREAMING_ERROR,
  SET_TYPEAHEAD_STREAMING_SOCKET_STATUS,
  SET_TYPEAHEAD_USER_SELECTED_DATES,
  SET_TYPEAHEAD_USER_SELECTED_OCCUPANCY,
} from './actionConstants'
import uuidV4 from 'lib/string/uuidV4Utils'
import type { AppAction } from 'actions/types/ActionTypes'
import { deleteRecentSearch, getRecentSearches, saveRecentSearch } from 'components/Search/utils'
import { setFlightSessionRecentSearch } from 'lib/flights/flightUtils'
import {
  addSearchSocketListener,
  removeAllSearchSocketListenersAndSafelyDisconnect,
  addStateListener,
} from 'lib/search/searchSocketListeners'
import { emitSocketMessage, clearLastMessage } from 'lib/search/socketEmitter'
import config from 'constants/config'
import { mapTypeaheadEndEventToSearchItemV2, mapTypeaheadLocation, mapTypeaheadResultToSearchItemV2, mapTypeaheadSuggestionToSearchItemV2 } from 'api/mappers/Search/typeaheadStreamingMap'
import type {
  TypeaheadLocationResponse,
  TypeaheadBedroomsResponse,
  TypeaheadAmenitiesResponse,
  TypeaheadInclusionsResponse,
  TypeaheadTravelDatesResponse,
  TypeaheadOccupancyResponse,
  TypeaheadPriceResponse,
  TypeaheadTagsResponse,
  TypeaheadSortByResponse,
  TypeaheadEndResponse,
  TypeaheadErrorResponse,
  TypeaheadNaturalLanguageSuggestionsV2Response,
  TypeaheadNaturalLanguageResultsV2Response,
  TypeaheadChainsResponse,
  TypeaheadPropertyResponseV2,
} from '@luxuryescapes/contract-search'

export function createSearch(
  action: { searchKey: string, searchId: string, searchContext?: App.SearchContext, searchEvent?: App.SearchEvent },
): AppAction {
  const { searchKey, searchId, searchContext, searchEvent } = action

  return {
    type: CREATE_SEARCH,
    key: searchKey,
    searchContext,
    searchEvent,
    searchId,
  }
}

export function createSearchSession(
  action: { searchKey: string, searchSessionId: string },
): AppAction {
  const { searchKey, searchSessionId } = action

  return {
    type: CREATE_SEARCH_SESSION,
    key: searchKey,
    searchSessionId,
  }
}

export function setSearchEvent(
  searchKey: string,
  options: { newSearchId?: string, searchContext?: App.SearchContext, searchEvent?: App.SearchEvent } = {},
): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const { newSearchId, searchContext, searchEvent } = options

    const searchId = state.search.searches[searchKey]?.searchId ?? newSearchId ?? uuidV4()

    dispatch(createSearch({ searchKey, searchId, searchContext, searchEvent }))
  }
}

export function setSearchSession(
  searchSessionKey: string,
  options: { searchSessionId: string },
): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const { searchSessionId: newSearchSessionId } = options

    const searchSessionId = state.search.searchSessions[searchSessionKey]?.searchSessionId ?? newSearchSessionId ?? uuidV4()

    dispatch(createSearchSession({ searchKey: searchSessionKey, searchSessionId }))
  }
}

export function fetchRecentSearches(): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (state.auth.recentSearches.length > 0) {
      // already updated this, don't need to do it again
      // it should always be in sync after initial fetch
      return
    }

    const recentSearches = getRecentSearches(state.auth.account.memberId)

    dispatch({
      type: FETCH_RECENT_SEARCHES,
      data: recentSearches,
    })
  }
}

export function addRecentSearch(
  searchItem: App.SearchItem,
  dates: App.SearchDates,
  rooms: Array<App.Occupants>,
): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    saveRecentSearch(searchItem, dates, rooms, state.auth.account.memberId)

    dispatch({
      type: SET_RECENT_SEARCHES,
      // our source of truth for recent search is local storage (for now?)
      // so once we save it, get the whole list
      data: getRecentSearches(state.auth.account.memberId),
    })
  }
}

export function removeRecentSearch(
  id: string,
): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    deleteRecentSearch(id, state.auth.account.memberId)

    dispatch({
      type: SET_RECENT_SEARCHES,
      // our source of truth for recent search is local storage (for now?)
      // so once we save it, get the whole list
      data: getRecentSearches(state.auth.account.memberId),
    })
  }
}

export function clearRecentSearches(): AppAction {
  return (dispatch) => {
    dispatch({
      type: SET_RECENT_SEARCHES,
      data: [],
    })
  }
}

export function clearAllSearchState(): AppAction {
  return (dispatch) => {
    // Clear Redux state for recent searches
    dispatch(clearRecentSearches())

    // Clear localStorage flight session data
    setFlightSessionRecentSearch({
      flights: [],
    })
  }
}

// Typeahead Streaming Actions

export function startTypeaheadStreaming(typeaheadId: string): AppAction {
  return {
    type: TYPEAHEAD_STREAMING_START,
    typeaheadId,
  }
}

export function updateTypeaheadStreaming(
  action: {
    typeaheadId: string
    updates: App.TypeaheadStreamingUpdates
    timestamp: string
    input: string
    extractedDataType: App.TypeaheadStreamingFieldType
  },
): AppAction {
  const { typeaheadId, updates, timestamp, input, extractedDataType } = action

  return {
    type: TYPEAHEAD_STREAMING_UPDATE,
    typeaheadId,
    updates,
    timestamp,
    input,
    extractedDataType,
  }
}

export function completeTypeaheadStreaming(
  action: { typeaheadId: string; input: string },
): AppAction {
  const { typeaheadId, input } = action

  return {
    type: TYPEAHEAD_STREAMING_COMPLETE,
    typeaheadId,
    input,
  }
}

export function errorTypeaheadStreaming(typeaheadId: string): AppAction {
  return {
    type: TYPEAHEAD_STREAMING_ERROR,
    typeaheadId,
  }
}

export function setTypeaheadUserSelectedDates(
  action: {
    typeaheadId: string
    dates: App.TypeaheadExtractedTravelDates
  },
): AppAction {
  const { typeaheadId, dates } = action

  return {
    type: SET_TYPEAHEAD_USER_SELECTED_DATES,
    typeaheadId,
    dates,
  }
}

export function setTypeaheadUserSelectedOccupancy(
  action: {
    typeaheadId: string
    occupancy: Array<App.TypeaheadExtractedOccupancy>
  },
): AppAction {
  const { typeaheadId, occupancy } = action

  return {
    type: SET_TYPEAHEAD_USER_SELECTED_OCCUPANCY,
    typeaheadId,
    occupancy,
  }
}

export function streamTypeahead(
  action: {
    input: string
    typeaheadId: string
    listenerId: string
    types?: Array<string>
    limit?: number
    isLuxPlusMember?: boolean
    rollout?: Array<string>
    temporaryDoNotExtract?: boolean
  },
): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const { input, typeaheadId, listenerId, types, limit, isLuxPlusMember, rollout, temporaryDoNotExtract } = action

    // Register state listener for cleanup tracking
    addStateListener({ eventGroupKey: typeaheadId, listenerId })

    const currentData = state.search.typeaheadStreamingData[typeaheadId]
    const isListening = currentData?.socketListenersStatus === 'listening'

    // Helper to check if event belongs to current request
    const isCurrentRequest = (responseTypeaheadId: string): boolean => {
      return responseTypeaheadId === typeaheadId
    }

    const handleResults = (response: TypeaheadNaturalLanguageResultsV2Response) => {
      if (isCurrentRequest(response.typeaheadId)) {
        const searchItems = response.results.map(mapTypeaheadResultToSearchItemV2)
        dispatch(
          updateTypeaheadStreaming({
            typeaheadId: response.typeaheadId,
            updates: { results: searchItems },
            timestamp: response.requestTimestamp,
            input: response.input,
            extractedDataType: 'results',
          }),
        )
      }
    }

    const handleNaturalLanguageSuggestions = (
      response: TypeaheadNaturalLanguageSuggestionsV2Response,
    ) => {
      if (isCurrentRequest(response.typeaheadId)) {
        const searchItems = response.suggestions.map(mapTypeaheadSuggestionToSearchItemV2)
        dispatch(
          updateTypeaheadStreaming({
            typeaheadId: response.typeaheadId,
            updates: { naturalLanguageSuggestions: searchItems },
            timestamp: response.requestTimestamp,
            input: response.input,
            extractedDataType: 'naturalLanguageSuggestions',
          }),
        )
      }
    }

    const handleLocation = (response: TypeaheadLocationResponse) => {
      if (isCurrentRequest(response.typeaheadId)) {
        dispatch(
          updateTypeaheadStreaming({
            typeaheadId: response.typeaheadId,
            updates: { location: mapTypeaheadLocation(response.location) },
            timestamp: response.requestTimestamp,
            input: response.input,
            extractedDataType: 'location',
          }),
        )
      }
    }

    const handleProperty = (response: TypeaheadPropertyResponseV2) => {
      if (isCurrentRequest(response.typeaheadId)) {
        const propertyId = response.property?.propertyId
        const propertyType = response.property?.propertyType
        dispatch(
          updateTypeaheadStreaming({
            typeaheadId: response.typeaheadId,
            updates: { property: propertyId && propertyType ? { propertyId, propertyType } : undefined },
            timestamp: response.requestTimestamp,
            input: response.input,
            extractedDataType: 'property',
          }),
        )
      }
    }

    const handleBedrooms = (response: TypeaheadBedroomsResponse) => {
      if (isCurrentRequest(response.typeaheadId)) {
        dispatch(
          updateTypeaheadStreaming({
            typeaheadId: response.typeaheadId,
            updates: { bedrooms: response.bedrooms },
            timestamp: response.requestTimestamp,
            input: response.input,
            extractedDataType: 'bedrooms',
          }),
        )
      }
    }

    const handleAmenities = (response: TypeaheadAmenitiesResponse) => {
      if (isCurrentRequest(response.typeaheadId)) {
        dispatch(
          updateTypeaheadStreaming({
            typeaheadId: response.typeaheadId,
            updates: { amenities: response.amenities },
            timestamp: response.requestTimestamp,
            input: response.input,
            extractedDataType: 'amenities',
          }),
        )
      }
    }

    const handleInclusions = (response: TypeaheadInclusionsResponse) => {
      if (isCurrentRequest(response.typeaheadId)) {
        dispatch(
          updateTypeaheadStreaming({
            typeaheadId: response.typeaheadId,
            updates: { inclusions: response.inclusions },
            timestamp: response.requestTimestamp,
            input: response.input,
            extractedDataType: 'inclusions',
          }),
        )
      }
    }

    const handleTravelDates = (response: TypeaheadTravelDatesResponse) => {
      if (isCurrentRequest(response.typeaheadId)) {
        dispatch(
          updateTypeaheadStreaming({
            typeaheadId: response.typeaheadId,
            updates: { travelDates: response.dates },
            timestamp: response.requestTimestamp,
            input: response.input,
            extractedDataType: 'travelDates',
          }),
        )
      }
    }

    const handleOccupancy = (response: TypeaheadOccupancyResponse) => {
      if (isCurrentRequest(response.typeaheadId)) {
        dispatch(
          updateTypeaheadStreaming({
            typeaheadId: response.typeaheadId,
            updates: { occupancy: response.occupancy },
            timestamp: response.requestTimestamp,
            input: response.input,
            extractedDataType: 'occupancy',
          }),
        )
      }
    }

    const handlePrice = (response: TypeaheadPriceResponse) => {
      if (isCurrentRequest(response.typeaheadId)) {
        dispatch(
          updateTypeaheadStreaming({
            typeaheadId: response.typeaheadId,
            updates: { price: response.price },
            timestamp: response.requestTimestamp,
            input: response.input,
            extractedDataType: 'price',
          }),
        )
      }
    }

    const handleTags = (response: TypeaheadTagsResponse) => {
      if (isCurrentRequest(response.typeaheadId)) {
        dispatch(
          updateTypeaheadStreaming({
            typeaheadId: response.typeaheadId,
            updates: { tags: response.tags },
            timestamp: response.requestTimestamp,
            input: response.input,
            extractedDataType: 'tags',
          }),
        )
      }
    }

    const handleChains = (response: TypeaheadChainsResponse) => {
      if (isCurrentRequest(response.typeaheadId)) {
        dispatch(
          updateTypeaheadStreaming({
            typeaheadId: response.typeaheadId,
            updates: { chains: response.chains, subChains: response.subChains },
            timestamp: response.requestTimestamp,
            input: response.input,
            extractedDataType: 'chains',
          }),
        )
      }
    }

    const handleSortBy = (response: TypeaheadSortByResponse) => {
      if (isCurrentRequest(response.typeaheadId)) {
        dispatch(
          updateTypeaheadStreaming({
            typeaheadId: response.typeaheadId,
            updates: { sortBy: response.sortBy },
            timestamp: response.requestTimestamp,
            input: response.input,
            extractedDataType: 'sortBy',
          }),
        )
      }
    }

    const handleEnd = (response: TypeaheadEndResponse) => {
      if (isCurrentRequest(response.typeaheadId)) {
        clearLastMessage(typeaheadId)
        const searchItem = mapTypeaheadEndEventToSearchItemV2(response)
        dispatch(
          updateTypeaheadStreaming({
            typeaheadId: response.typeaheadId,
            updates: { finalSearchItem: searchItem },
            timestamp: response.requestTimestamp,
            input: response.input,
            extractedDataType: 'finalSearchItem',
          }),
        )
        dispatch(completeTypeaheadStreaming({ typeaheadId: response.typeaheadId, input: response.input }))
        dispatch({
          type: SET_TYPEAHEAD_STREAMING_SOCKET_STATUS,
          typeaheadId,
          status: 'not-listening',
        })
        removeAllSearchSocketListenersAndSafelyDisconnect({ eventGroupKey: typeaheadId })
      }
    }

    const handleError = (_response: TypeaheadErrorResponse) => {
      clearLastMessage(typeaheadId)
      dispatch(errorTypeaheadStreaming(typeaheadId))
      dispatch({
        type: SET_TYPEAHEAD_STREAMING_SOCKET_STATUS,
        typeaheadId,
        status: 'not-listening',
      })
      removeAllSearchSocketListenersAndSafelyDisconnect({ eventGroupKey: typeaheadId })
    }

    // Register all socket listeners using searchSocketListeners utility (only once)
    if (!isListening) {
      // Set socket listener status to listening
      dispatch({
        type: SET_TYPEAHEAD_STREAMING_SOCKET_STATUS,
        typeaheadId,
        status: 'listening',
      })

      // Register all socket listeners
      addSearchSocketListener({
        eventGroupKey: typeaheadId,
        event: 'typeahead:response:results:v2',
        handler: handleResults,
      })
      addSearchSocketListener({
        eventGroupKey: typeaheadId,
        event: 'typeahead:response:suggestions:v2',
        handler: handleNaturalLanguageSuggestions,
      })
      addSearchSocketListener({
        eventGroupKey: typeaheadId,
        event: 'typeahead:response:location',
        handler: handleLocation,
      })
      addSearchSocketListener({
        eventGroupKey: typeaheadId,
        event: 'typeahead:response:property:v2',
        handler: handleProperty,
      })
      addSearchSocketListener({
        eventGroupKey: typeaheadId,
        event: 'typeahead:response:bedrooms',
        handler: handleBedrooms,
      })
      addSearchSocketListener({
        eventGroupKey: typeaheadId,
        event: 'typeahead:response:amenities',
        handler: handleAmenities,
      })
      addSearchSocketListener({
        eventGroupKey: typeaheadId,
        event: 'typeahead:response:inclusions',
        handler: handleInclusions,
      })
      addSearchSocketListener({
        eventGroupKey: typeaheadId,
        event: 'typeahead:response:travel-dates',
        handler: handleTravelDates,
      })
      addSearchSocketListener({
        eventGroupKey: typeaheadId,
        event: 'typeahead:response:occupancy',
        handler: handleOccupancy,
      })
      addSearchSocketListener({
        eventGroupKey: typeaheadId,
        event: 'typeahead:response:price',
        handler: handlePrice,
      })
      addSearchSocketListener({
        eventGroupKey: typeaheadId,
        event: 'typeahead:response:tags',
        handler: handleTags,
      })
      addSearchSocketListener({
        eventGroupKey: typeaheadId,
        event: 'typeahead:response:chains',
        handler: handleChains,
      })
      addSearchSocketListener({
        eventGroupKey: typeaheadId,
        event: 'typeahead:response:sort-by',
        handler: handleSortBy,
      })
      addSearchSocketListener({
        eventGroupKey: typeaheadId,
        event: 'typeahead:response:end',
        handler: handleEnd,
      })
      addSearchSocketListener({
        eventGroupKey: typeaheadId,
        event: 'typeahead:response:error',
        handler: handleError,
      })
    }

    // Always start streaming session for each new request (resets isComplete and completedInput)
    dispatch(startTypeaheadStreaming(typeaheadId))

    const streamingData = state.search.typeaheadStreamingData[typeaheadId]
    const dates = streamingData?.userSelectedDates
    const occupancy = streamingData?.userSelectedOccupancy

    // Always emit the request on each call (even if listeners are already set up)
    emitSocketMessage(typeaheadId, 'typeahead:request:extract', {
      input,
      brand: config.BRAND,
      region: state.geo.currentRegionCode,
      typeaheadId,
      types,
      limit,
      isLuxPlusMember,
      rollout,
      temporaryDoNotExtract,
      dates,
      occupancy,
    })
  }
}
